import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  X,
  AlertTriangle,
  Flag,
} from "lucide-react";
import { useState } from "react";

import { UserSearch } from "~/components/admin/user-search";
import { EmptyState } from "~/components/ui/empty-state";
import { Switch } from "~/components/ui/switch";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminFeatureFlagsQueryOptions,
  adminFeatureFlagQueryKeys,
  updateFeatureFlagFn,
} from "~/lib/functions/admin-feature-flags";
import type { AdminFeatureFlagRow, FlagUser } from "~/lib/shared/feature-flags";

export const Route = createFileRoute("/admin/feature-flags/")({
  staticData: { title: "Feature Flags" },
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "feature-flags:read"),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(adminFeatureFlagsQueryOptions);
  },
  component: FeatureFlagsAdminPage,
});

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

type FlagStatus = "disabled" | "whitelist" | "all_users";

function getFlagStatus(flag: AdminFeatureFlagRow): FlagStatus {
  if (!flag.enabled) return "disabled";
  if (flag.allUsers) return "all_users";
  return "whitelist";
}

function StatusBadge({ status }: { status: FlagStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        status === "all_users" && "bg-emerald-100 text-emerald-700",
        status === "whitelist" && "bg-amber-100 text-amber-700",
        status === "disabled" && "bg-gray-100 text-gray-600",
      )}
    >
      {status === "all_users" && "All users"}
      {status === "whitelist" && "Whitelist only"}
      {status === "disabled" && "Disabled"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Summary sentence
// ---------------------------------------------------------------------------

function FlagSummary({ flag }: { flag: AdminFeatureFlagRow }) {
  if (!flag.enabled) {
    return (
      <p className="text-sm text-muted-foreground">
        This flag is <span className="font-medium">disabled</span> for everyone.
      </p>
    );
  }
  if (flag.allUsers) {
    return (
      <p className="text-sm text-muted-foreground">
        This flag is <span className="font-medium">enabled for all users</span>.
      </p>
    );
  }
  if (flag.whitelistedUsers.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-sm text-amber-600">
        <AlertTriangle className="h-3.5 w-3.5" />
        This flag is enabled but no users have access. Enable for all users or
        add specific users.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      This flag is{" "}
      <span className="font-medium">
        enabled for {flag.whitelistedUsers.length} specific user
        {flag.whitelistedUsers.length !== 1 ? "s" : ""}
      </span>
      .
    </p>
  );
}

// ---------------------------------------------------------------------------
// Expanded flag detail
// ---------------------------------------------------------------------------

function FlagDetail({ flag }: { flag: AdminFeatureFlagRow }) {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: updateFeatureFlagFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminFeatureFlagQueryKeys.all,
      });
    },
  });

  const handleToggleEnabled = () => {
    updateMutation.mutate({
      data: { key: flag.key, enabled: !flag.enabled },
    });
  };

  const handleToggleAllUsers = () => {
    updateMutation.mutate({
      data: { key: flag.key, allUsers: !flag.allUsers },
    });
  };

  const handleAddUser = (user: FlagUser) => {
    updateMutation.mutate({
      data: { key: flag.key, addUserIds: [user.id] },
    });
  };

  const handleRemoveUser = (userId: string) => {
    updateMutation.mutate({
      data: { key: flag.key, removeUserIds: [userId] },
    });
  };

  return (
    <div className="space-y-6 border-t bg-gray-50/50 px-6 py-5">
      <FlagSummary flag={flag} />

      {/* Toggles */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Enabled</div>
            <div className="text-xs text-muted-foreground">
              Master switch — disables the flag for everyone when off
            </div>
          </div>
          <Switch
            checked={flag.enabled}
            onCheckedChange={handleToggleEnabled}
            disabled={updateMutation.isPending}
          />
        </div>

        <div
          className={cn(
            "flex items-center justify-between",
            !flag.enabled && "opacity-40",
          )}
        >
          <div>
            <div className="text-sm font-medium">All Users</div>
            <div className="text-xs text-muted-foreground">
              Enable for every user — whitelist is not evaluated
            </div>
          </div>
          <Switch
            checked={flag.allUsers}
            onCheckedChange={handleToggleAllUsers}
            disabled={!flag.enabled || updateMutation.isPending}
          />
        </div>
      </div>

      {/* Whitelist */}
      <div
        className={cn(
          !flag.enabled && "opacity-40",
          flag.allUsers && "opacity-50",
        )}
      >
        <div className="mb-3 text-sm font-medium">
          Whitelisted Users
          {flag.allUsers && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              (not evaluated — all users have access)
            </span>
          )}
        </div>

        {!flag.allUsers && flag.enabled && (
          <div className="mb-3">
            <UserSearch
              excludeIds={flag.whitelistedUsers.map((u) => u.id)}
              onSelect={handleAddUser}
            />
          </div>
        )}

        {flag.whitelistedUsers.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No users whitelisted. Search above to add users.
          </p>
        ) : (
          <ul className="space-y-2">
            {flag.whitelistedUsers.map((user) => (
              <li
                key={user.id}
                className="flex items-center justify-between rounded-lg border bg-white px-3 py-2"
              >
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {user.name}
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveUser(user.id)}
                  disabled={updateMutation.isPending}
                  className="rounded p-1 text-red-500 transition hover:bg-red-50"
                  title="Remove user"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function FeatureFlagsAdminPage() {
  const { data: flags, isLoading } = useQuery(adminFeatureFlagsQueryOptions);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!flags || flags.length === 0) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-medium text-foreground">
            Feature Flags
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage feature flag rollout and user access.
          </p>
        </div>
        <div className="rounded-xl border bg-white shadow-sm">
          <EmptyState
            icon={Flag}
            title="No feature flags yet"
            description="Flags are defined in code — check src/lib/shared/feature-flags.ts to register one."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-medium text-foreground">Feature Flags</h1>
        <p className="text-sm text-muted-foreground">
          Manage feature flag rollout and user access.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        {flags.map((flag) => {
          const isExpanded = expandedKey === flag.key;
          const status = getFlagStatus(flag);

          return (
            <div
              key={flag.key}
              className={cn("border-b last:border-0", isExpanded && "bg-white")}
            >
              {/* Row */}
              <button
                type="button"
                onClick={() => setExpandedKey(isExpanded ? null : flag.key)}
                className="flex w-full items-center gap-4 px-6 py-4 text-left transition hover:bg-gray-50"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">
                    {flag.label}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {flag.key}
                  </div>
                </div>
                <div className="hidden text-xs text-muted-foreground sm:block sm:max-w-xs sm:truncate">
                  {flag.description}
                </div>
                <StatusBadge status={status} />
                <div className="w-16 text-right text-xs text-muted-foreground">
                  {flag.whitelistedUsers.length} user
                  {flag.whitelistedUsers.length !== 1 ? "s" : ""}
                </div>
              </button>

              {/* Expanded detail */}
              {isExpanded && <FlagDetail flag={flag} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
